import React, { useEffect, useState, useMemo } from "react";
import { Footer, Navbar, PromoCodeSection } from "../components";
import { useSelector, useDispatch } from "react-redux";
import { addCart, delCart, setCart } from "../redux/action";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "../utils/base_url";
import { motion } from "framer-motion";
import '../styles/index.css';
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";


const ShowCart = ({
  state,
  stocks,
  stockError,
  disabledQty,
  addItem,
  removeItem,
  formatName,
  initiateCheckout,
  priceMap,
  totals,
  loadingPrices,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
}) => {
  const navigate = useNavigate();

  const rawSubtotal = totals?.effective_subtotal != null
    ? Number(totals.effective_subtotal)
    : (totals?.product_subtotal != null ? Number(totals.product_subtotal) : 0);

  const couponDiscount = totals?.discount_amount != null
    ? Number(totals.discount_amount)
    : (totals?.coupon_discount != null
      ? Number(totals.coupon_discount)
      : (appliedCoupon?.discount_amount ? Number(appliedCoupon.discount_amount) : 0));

  const discountedSubtotal = totals?.discounted_subtotal != null
    ? Number(totals.discounted_subtotal)
    : Math.max(0, rawSubtotal - couponDiscount);

  return (
    <section className="h-100 gradient-custom-themed">
      <div className="container py-5">
        <div className="row d-flex justify-content-center my-4">

          {/* Cart items */}
          <div className="col-lg-8 col-md-10 col-12 mb-4">
            <div className="card mb-4 rounded-theme shadow-theme">
              <div className="card-header py-3 bg-light">
                <h5 className="mb-0 text-theme-dark">Item List</h5>
              </div>
              <div className="card-body p-3">
                {state.map((item) => {
                  const line = priceMap[item.id];
                  // ✅ No frontend price math — trust the API. Fall back to a
                  // loading placeholder rather than guessing the amount.
                  const unitPrice = line ? Number(line.has_offer ? line.offer_price : line.unit_price) : null;
                  const originalPrice = line && line.has_offer ? Number(line.unit_price) : null;
                  const lineTotal = line ? Number(line.line_total) : null;

                  return (
                    <div
                      key={item.id}
                      className="mb-3 pb-3 border-bottom border-theme-light"
                    >
                      <div className="row align-items-center g-2">
                        {/* CLICKABLE AREA */}
                        <div
                          className="col-12 col-sm-8"
                          onClick={() => navigate(`/product/${item.id}`)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="row align-items-center g-2">
                            <div className="col-4 col-sm-3 text-center">
                              <img
                                src={item.product_image}
                                alt={formatName(item.product_name)}
                                className="rounded-theme img-fluid"
                                style={{ maxHeight: "90px", objectFit: "contain" }}
                              />
                            </div>

                            <div className="col-8 col-sm-9">
                              <p className="text-theme-dark fw-semibold mb-1" style={{ fontSize: "1rem" }}>
                                {formatName(item.product_name)}
                              </p>

                              {item.quantity && item.quantity_unit && (
                                <p className="text-secondary small mb-1">
                                  {["size", "color"].includes(item.quantity_unit?.toLowerCase())
                                    ? item.quantity
                                    : `${item.quantity} ${item.quantity_unit}`}
                                </p>
                              )}

                              <p className="text-theme-muted mb-1" style={{ fontSize: "0.9rem" }}>
                                {unitPrice === null ? (
                                  <span className="placeholder-glow">
                                    <span className="placeholder col-3"></span>
                                  </span>
                                ) : (
                                  <>
                                    ₹ {unitPrice.toLocaleString()}
                                    {originalPrice && (
                                      <span style={{
                                        fontSize: "0.8rem",
                                        textDecoration: "line-through",
                                        color: "#ff0000ff",
                                        marginLeft: "6px",
                                      }}>
                                        ₹ {originalPrice.toLocaleString()}
                                      </span>
                                    )}
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* QUANTITY CONTROLS */}
                        <div className="col-12 col-sm-4 d-flex align-items-center justify-content-between justify-content-sm-end gap-2">
                          <div className="input-group input-group-sm quantity-controls" style={{ maxWidth: "120px" }}>
                            {item.qty > 1 ? (
                              <button
                                className="btn btn-themed-outline"
                                onClick={(e) => { e.stopPropagation(); removeItem(item); }}
                                aria-label={`Remove one ${item.product_name}`}
                              >
                                <i className="fas fa-minus"></i>
                              </button>
                            ) : (
                              <button
                                className="btn btn-themed-outline"
                                onClick={(e) => { e.stopPropagation(); removeItem(item); }}
                                aria-label={`Remove ${item.product_name} from cart`}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}

                            <span className="input-group-text qty-display">{item.qty}</span>
                            <button
                              className="btn btn-themed-outline"
                              onClick={(e) => { e.stopPropagation(); addItem(item); }}
                              disabled={disabledQty[item.id]}
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          </div>

                          <p className="text-theme-dark fw-bold mb-0 ps-3" style={{ minWidth: "90px", textAlign: "right", fontSize: "1rem" }}>
                            {lineTotal === null ? "…" : `₹ ${lineTotal.toLocaleString()}`}
                          </p>

                          {stockError[item.id] && (
                            <motion.div
                              initial={{ x: 80, opacity: 0 }}
                              animate={{ x: [80, 0, -6, 6, -6, 6, 0], opacity: 1 }}
                              transition={{ duration: 0.6, ease: "easeOut" }}
                              style={{
                                position: "absolute", bottom: "40px", right: "-13px",
                                transform: "translateX(-50%)", padding: "6px 14px",
                                background: "#dc3545", color: "white", borderRadius: "6px 0 0 6px",
                                fontSize: "0.8rem", fontWeight: 600, zIndex: 99,
                                whiteSpace: "nowrap", boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                              }}
                            >
                              Stock limit reached!
                            </motion.div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="col-lg-4 col-md-8 col-12 mb-4">
            {/* Promo Code Box */}
            <PromoCodeSection
              cartItems={state}
              appliedCoupon={appliedCoupon}
              onApplyCoupon={onApplyCoupon}
              onRemoveCoupon={onRemoveCoupon}
              disabled={loadingPrices}
            />

            <div className="card rounded-theme shadow-theme">
              <div className="card-header py-3 bg-light border-theme-light">
                <h5 className="mb-0 text-theme-dark">Order Summary</h5>
              </div>
              <div className="card-body p-3">
                <ul className="list-group list-group-flush mb-3">
                  <li className="list-group-item d-flex justify-content-between px-0">
                    Product Amount
                    <span>
                      {loadingPrices || !totals ? "…" : `₹${Math.round(rawSubtotal).toLocaleString()}`}
                    </span>
                  </li>

                  {totals?.total_savings > 0 && (
                    <li className="list-group-item d-flex justify-content-between px-0 text-success">
                      You saved (Offers)
                      <span>-₹{Math.round(totals.total_savings).toLocaleString()}</span>
                    </li>
                  )}

                  {couponDiscount > 0 && (
                    <li className="list-group-item d-flex justify-content-between px-0 text-success fw-semibold">
                      <span>Promo ({appliedCoupon?.code || "Coupon"})</span>
                      <span>-₹{Math.round(couponDiscount).toLocaleString()}</span>
                    </li>
                  )}

                  {couponDiscount > 0 && (
                    <li className="list-group-item d-flex justify-content-between px-0 text-secondary" style={{ fontSize: "0.9rem" }}>
                      <span>Discounted Subtotal</span>
                      <span>₹{Math.round(discountedSubtotal).toLocaleString()}</span>
                    </li>
                  )}

                  <li className="list-group-item d-flex justify-content-between px-0 align-items-center">
                    <span>Courier Charge</span>
                    <span style={{ fontSize: "0.78rem", color: "#7a563a", fontStyle: "italic" }}>
                      calculated at checkout
                    </span>
                  </li>

                  <li className="list-group-item d-flex justify-content-between px-0 fw-bold align-items-center">
                    <span>Sub Total</span>
                    <span>
                      {loadingPrices || !totals ? "…" : `₹${Math.round(discountedSubtotal).toLocaleString()}`}{" "}
                      <span style={{ fontSize: "0.75rem", fontWeight: 400, color: "#7a563a" }}>
                        + courier
                      </span>
                    </span>
                  </li>
                </ul>

                <button
                  className="btn-themed btn-lg w-100 text-center"
                  onClick={initiateCheckout}
                  disabled={loadingPrices}
                >
                  Go to checkout
                </button>

                <p style={{ fontSize: "0.75rem", color: "#7a563a", textAlign: "center", marginTop: "8px", marginBottom: 0 }}>
                  Delivery charge will be added based on your pincode
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const Cart = () => {
  const [stocks, setStocks] = useState({});
  const [stockError, setStockError] = useState({});
  const [disabledQty, setDisabledQty] = useState({});
  const [priceSummary, setPriceSummary] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    try {
      const saved = sessionStorage.getItem("applied_coupon");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const state = useSelector((state) => state.handleCart);
  const dispatch = useDispatch();

  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Single source of truth for pricing — includes promo coupon code
  useEffect(() => {
    if (!state.length) {
      setPriceSummary(null);
      return;
    }

    let cancelled = false;
    const fetchPriceSummary = async () => {
      try {
        setLoadingPrices(true);
        const payload = {
          items: state.map((item) => ({ product: item.id, quantity: item.qty })),
        };
        if (appliedCoupon?.code) {
          payload.coupon_code = appliedCoupon.code;
        }

        const res = await api.post("cart/price-summary/", payload);
        if (!cancelled) {
          setPriceSummary(res.data);
          // If the backend adjusted discount or validated coupon
          if (appliedCoupon && res.data.totals?.discount_amount !== undefined) {
            const newDiscount = Number(res.data.totals.discount_amount);
            setAppliedCoupon((prev) => {
              if (!prev) return null;
              if (prev.discount_amount === newDiscount) return prev;
              return { ...prev, discount_amount: newDiscount };
            });
          }
        }
      } catch (err) {
        console.error("Price summary fetch failed", err);
        if (!cancelled) {
          // If coupon is invalid against updated cart (e.g. min order value not met)
          if (appliedCoupon && err.response?.data?.message) {
            toast.error(`Coupon removed: ${err.response.data.message}`);
            setAppliedCoupon(null);
            sessionStorage.removeItem("applied_coupon");
          }
          setPriceSummary(null);
        }
      } finally {
        if (!cancelled) setLoadingPrices(false);
      }
    };

    fetchPriceSummary();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, appliedCoupon?.code]);

  const handleApplyCoupon = (couponData) => {
    setAppliedCoupon(couponData);
    sessionStorage.setItem("applied_coupon", JSON.stringify(couponData));
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    sessionStorage.removeItem("applied_coupon");
  };

  const priceMap = useMemo(() => {
    const map = {};
    priceSummary?.items?.forEach((i) => { map[i.product] = i; });
    return map;
  }, [priceSummary]);

  const addItem = (product) => {
    const availableStock = stocks[product.id];
    const nextQty = product.qty + 1;

    if (availableStock && nextQty > availableStock) {
      setStockError(prev => ({ ...prev, [product.id]: true }));
      setDisabledQty(prev => ({ ...prev, [product.id]: true }));

      setTimeout(() => {
        setStockError(prev => ({ ...prev, [product.id]: false }));
        setDisabledQty(prev => ({ ...prev, [product.id]: false }));
      }, 2000);

      return;
    }

    dispatch(addCart(product));
  };

  const initiateCheckout = async () => {
    const token = sessionStorage.getItem("access");
    if (!token) {
      sessionStorage.setItem("redirect_toast", "Please login first to continue checkout");
      sessionStorage.setItem("login_redirect", JSON.stringify({
        pathname: location.pathname, search: location.search, state: location.state,
      }));
      return navigate("/login");
    }

    const userId = user.id;
    const cartPayload = state.map((item) => ({ product_id: item.id, qty: item.qty }));

    try {
      const res = await api.post("checkout-initiate/", { user_id: userId, cart: cartPayload });

      if (!res.data.status) {
        toast.error(res.data.message || "Checkout failed");
        return;
      }

      if (res.data.updated_items?.length || res.data.removed_items?.length) {
        toast.error("Cart updated due to stock change. Please review and click checkout again.");

        const final = res.data.reserved_items.map((i) => ({
          id: i.product_id,
          qty: i.qty,
          product_name: i.product_name,
          price: Number(i.price) || 0,
          offer_price: i.offer_price ? Number(i.offer_price) : null,
          product_image: i.product_image,
        }));

        dispatch(setCart(final));
        return;
      }

      navigate("/checkout");
    } catch (err) {
      console.error("initiateCheckout error:", err);
      toast.error("Unable to start checkout");
    }
  };

  const formatName = (text) => {
    if (!text) return "";
    const cleaned = text.replace(/_/g, " ").toLowerCase();
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  const removeItem = (product) => {
    dispatch(delCart(product));
    setStockError(prev => ({ ...prev, [product.id]: false }));
  };

  const EmptyCart = () => (
    <div className="container">
      <div className="row">
        <div className="col-12 py-5 bg-light text-center rounded-theme">
          <h4 className="p-3 display-5" style={{ color: "#4A1F1A" }}>
            Your Cart is Empty
          </h4>
          <Link to="/product" className="btn btn-outline-themed mx-4">
            <i className="fa fa-arrow-left"></i> Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const getStock = async () => {
      try {
        const stockData = {};
        for (const item of state) {
          const res = await api.get(`stock/?product_id=${item.id}`);
          stockData[item.id] = res.data.stock;
        }
        setStocks(stockData);
      } catch (error) {
        console.error("Stock fetch error:", error);
      }
    };

    if (state.length > 0) getStock();
  }, [state]);

  return (
    <>
      <Navbar />
      <div className="container my-3 py-3">
        <h1 className="text-center text-theme-dark">Cart</h1>
        <hr className="border-theme-light" />
        {state.length > 0 ? (
          <ShowCart
            state={state}
            stocks={stocks}
            stockError={stockError}
            disabledQty={disabledQty}
            addItem={addItem}
            removeItem={removeItem}
            formatName={formatName}
            initiateCheckout={initiateCheckout}
            priceMap={priceMap}
            totals={priceSummary?.totals}
            loadingPrices={loadingPrices}
            appliedCoupon={appliedCoupon}
            onApplyCoupon={handleApplyCoupon}
            onRemoveCoupon={handleRemoveCoupon}
          />
        ) : <EmptyCart />}
      </div>
      <Footer />

      <style>{`
        :root {
          --brown-dark: #4A1F1A;
          --brown-darker: #8A4B27;
          --brown-light: #f1e6d4;
          --cream-bg: #E9DCC3;
          --text-dark: #4A1F1A;
          --text-medium: #7a563a;
          --text-muted: #000000ff;
        }
        body, html, .container { background-color: var(--cream-bg); }
        .text-theme-dark { color: var(--text-dark); }
        .text-theme-muted { color: var(--text-muted); }
        .bg-light { background-color: var(--cream-bg) !important; }
        .border-theme-light { border-color: var(--brown-light) !important; }
        .btn-themed {
          background-color: var(--brown-dark); color: #fff !important; border-radius: 8px;
          font-weight: 600; padding: 12px 20px; border: none; display: flex;
          align-items: center; justify-content: center; text-align: center; cursor: pointer; width: 100%;
        }
        .btn-themed:hover, .btn-themed:focus {
          background-color: var(--brown-darker); text-decoration: none; outline: none;
          box-shadow: 0 0 10px rgba(122, 86, 58, 0.6);
        }
        .btn-themed-outline {
          background-color: transparent; color: var(--brown-dark) !important;
          border: 1.5px solid var(--brown-dark); border-radius: 25px; font-weight: 600;
          padding: 5px 10px; cursor: pointer; user-select: none; transition: all 0.3s ease;
          display: inline-flex; align-items: center; justify-content: center; min-width: 30px; height: 32px;
        }
        .btn-themed-outline:hover, .btn-themed-outline:focus {
          background-color: var(--brown-dark); color: #8A4B27 !important; border-color: var(--brown-dark);
          outline: none; box-shadow: 0 0 8px rgba(122, 86, 58, 0.6); text-decoration: none;
        }
        .rounded-theme { border-radius: 12px !important; }
        .shadow-theme { box-shadow: 0 5px 14px #8A4B27 !important; }
        .gradient-custom-themed { background: var(--cream-bg); }
        .input-group-text.qty-display {
          background: var(--cream-bg); color: var(--text-dark); font-weight: 600;
          min-width: 35px; text-align: center; border: 1.5px solid var(--brown-light);
          border-radius: 0 0.4rem 0.4rem 0; padding: 0.35rem 0.5rem;
        }
        @media (max-width: 768px) {
          .card-body > div { flex-direction: column !important; align-items: flex-start !important; }
          .input-group.input-group-sm.quantity-controls { max-width: 100% !important; width: 100%; margin-top: 10px; }
          .qty-display { min-width: 45px !important; }
          .btn-themed-outline { min-width: 35px; height: 35px; }
          .btn-themed { padding: 10px; font-size: 1rem; }
        }
        .quantity-controls {
          display: flex; align-items: center; justify-content: space-between;
          border: 1.5px solid var(--brown-dark); border-radius: 10px; padding: 4px;
          background: #fff; gap: 4px; transition: box-shadow 0.25s ease, transform 0.15s ease;
        }
        .quantity-controls:hover { box-shadow: 0 0 8px rgba(46, 125, 50, 0.35); }
        .quantity-controls .btn-themed-outline { border: none; background: transparent; padding: 6px; min-width: 30px; height: 30px; }
        .quantity-controls .qty-display { border: none; background: transparent; font-weight: 600; min-width: 32px; text-align: center; padding: 0; }
        @media (max-width: 576px) {
          .quantity-controls { width: 100%; max-width: 120px; padding: 6px; border-radius: 8px; }
          .quantity-controls .btn-themed-outline { min-width: 36px; height: 36px; font-size: 1rem; }
          .quantity-controls .qty-display { min-width: 40px; font-size: 1rem; }
        }
        .quantity-controls button:active { transform: scale(0.94); }
        .quantity-controls button:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </>
  );
};

export default Cart;